/**
 * Slash-command palette — pops up above the chat input when the user
 * types ``/`` at the start of a message. The list is dynamic: it
 * reflects whatever the live Claude Code CLI install exposes via
 * ``GET /api/slash-commands`` (built-ins + user/project skills +
 * plugin commands), so it stays in sync with what actually works
 * inside cowork.
 *
 * Purely presentational — ``ChatInput`` owns the open/close state,
 * the filtered list, the highlighted cursor, and the selection
 * callback. That keeps focus + keyboard wiring close to the textarea.
 */

import { useEffect, useRef } from "react";
import type { SlashCommand } from "./slashCommands";

interface SlashCommandMenuProps {
  /** Filtered command list to display. Empty → "no matches" empty state. */
  commands: SlashCommand[];
  /** Index of the currently highlighted command (keyboard cursor). */
  activeIndex: number;
  /** User invoked this command — ``ChatInput`` should insert it into the textarea. */
  onSelect: (cmd: SlashCommand) => void;
  /** User hovered an item — keep keyboard + mouse cursors in sync. */
  onHoverIndex: (index: number) => void;
  /** Raw query (text after the slash) for the header copy. */
  query: string;
  /** True while the initial fetch is still in flight. */
  loading: boolean;
  /** Non-null if the fetch failed; rendered as a footer warning. */
  error: string | null;
}

const SCOPE_LABEL: Record<SlashCommand["scope"], string> = {
  builtin: "Built-in",
  user: "User",
  project: "Project",
  plugin: "Plugin",
};

const SCOPE_STYLE_ACTIVE: Record<SlashCommand["scope"], string> = {
  builtin: "bg-primary text-on-primary",
  user: "bg-oxy text-white",
  project: "bg-gold text-space",
  plugin: "bg-hydra text-white",
};

const SCOPE_STYLE_IDLE: Record<SlashCommand["scope"], string> = {
  // ``text-jove`` (theme-adaptive accent) not the fixed-dark ``jove-400`` —
  // the latter is #1F095D and vanishes on the dark tint in dark mode.
  builtin: "bg-jove-30 text-jove",
  user: "bg-oxy-50 text-oxy",
  project: "bg-gold/15 text-gold",
  plugin: "bg-hydra-50 text-hydra",
};

export function SlashCommandMenu({
  commands,
  activeIndex,
  onSelect,
  onHoverIndex,
  query,
  loading,
  error,
}: SlashCommandMenuProps) {
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the active row in view as the user arrows up/down.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      className="absolute bottom-full left-0 right-0 mb-2 max-h-[320px] overflow-hidden
                 rounded-2xl border border-space-30 bg-surface shadow-[0_12px_40px_rgba(31,9,93,0.12)]
                 origin-bottom animate-[scaleIn_120ms_ease-out] z-30 flex flex-col"
    >
      {/* Header — what we're filtering on + match count */}
      <div className="flex items-center justify-between gap-3 px-3.5 py-2 border-b border-space-30/60 bg-jove-10">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-primary text-on-primary font-mono text-[11px] font-bold leading-none shrink-0"
            aria-hidden
          >
            /
          </span>
          <span className="text-xs font-semibold text-jove truncate">
            {query ? `Matching "${query}"` : "Slash commands"}
          </span>
        </div>
        <span className="text-[10px] text-space-50 tabular-nums shrink-0">
          {loading
            ? "Loading…"
            : `${commands.length} ${commands.length === 1 ? "command" : "commands"}`}
        </span>
      </div>

      {loading && commands.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-space-50 flex items-center justify-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full border-2 border-jove-60 border-t-jove animate-spin"
            aria-hidden
          />
          Discovering commands from your Claude install…
        </div>
      ) : commands.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-space-50">
          {error
            ? `Could not load commands: ${error}`
            : "No matching commands."}
          <div className="mt-1.5">
            Press
            <kbd className="mx-1 px-1 py-0.5 bg-space-20 rounded text-space-50 font-mono">
              Esc
            </kbd>
            to dismiss.
          </div>
        </div>
      ) : (
        <ul
          ref={listRef}
          className="flex-1 overflow-y-auto py-1"
          // Prevent textarea blur when clicking the menu so selection works.
          onMouseDown={(e) => e.preventDefault()}
        >
          {commands.map((cmd, i) => {
            const isActive = i === activeIndex;
            const badgeLabel =
              cmd.scope === "plugin" && cmd.plugin
                ? cmd.plugin
                : SCOPE_LABEL[cmd.scope];
            return (
              <li
                key={cmd.name}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => onHoverIndex(i)}
                onClick={() => onSelect(cmd)}
                className={`group flex items-center gap-3 px-3.5 py-2 cursor-pointer transition-colors
                  ${isActive ? "bg-sel-surface" : "hover:bg-space-20/60"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-mono text-sm font-semibold tracking-tight truncate
                        ${isActive ? "text-sel-ink" : "text-jove"}`}
                    >
                      /{cmd.name}
                    </span>
                    {cmd.args && (
                      <span className="font-mono text-[11px] text-space-40 truncate">
                        {cmd.args}
                      </span>
                    )}
                  </div>
                  {cmd.description && (
                    <p className="text-xs text-space-50 truncate mt-0.5">
                      {cmd.description}
                    </p>
                  )}
                </div>

                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold shrink-0 px-1.5 py-0.5 rounded max-w-[140px] truncate
                    ${isActive ? SCOPE_STYLE_ACTIVE[cmd.scope] : SCOPE_STYLE_IDLE[cmd.scope]}`}
                  title={badgeLabel}
                >
                  {badgeLabel}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {commands.length > 0 && (
        <div className="flex items-center justify-between px-3.5 py-1.5 border-t border-space-30/60 bg-space-20/40 text-[10px] text-space-50">
          <div className="flex items-center gap-2">
            <kbd className="px-1 py-0.5 bg-surface rounded border border-space-30 font-mono">↑↓</kbd>
            <span>navigate</span>
            <kbd className="px-1 py-0.5 bg-surface rounded border border-space-30 font-mono">Tab</kbd>
            <span>insert</span>
            <kbd className="px-1 py-0.5 bg-surface rounded border border-space-30 font-mono">Enter</kbd>
            <span>insert &amp; send</span>
          </div>
          <kbd className="px-1 py-0.5 bg-surface rounded border border-space-30 font-mono">Esc</kbd>
        </div>
      )}
    </div>
  );
}
